/**
 * Customer theme adapter (MFE-v3-B-01) — DECISION-2 C-Hybrid.
 *
 * - `applyInitialCustomerTheme()` runs in `main.tsx` before React renders.
 * - Persists under `rpapp-customer-theme`; when unset, `prefers-color-scheme` decides.
 * - Dark mode uses `.dark` on `<html>`; explicit light uses `.light` when flag ON.
 *
 * SSOT: `pi-kiosk-shared/theme` (`themeContract.ts`).
 */

import {
  ThemeProvider as SharedThemeProvider,
  createThemeApi,
  THEME_STORAGE_KEYS,
  useTheme,
  type EffectiveTheme,
  type ThemePreference,
} from 'pi-kiosk-shared/theme';
import type { ReactNode } from 'react';
import type { RuntimeConfig } from '../config/runtimeConfig.js';

export type { EffectiveTheme, ThemePreference };

export { useTheme };

export interface ThemeProviderProps {
  children: ReactNode;
  lightOverrideEnabled?: boolean;
}

export function applyInitialCustomerTheme(_config: RuntimeConfig): void {
  createThemeApi(THEME_STORAGE_KEYS.customer, {
    lightOverrideEnabled: true,
  }).applyInitialTheme();
}

/** @deprecated Use `applyInitialCustomerTheme` — kept for existing imports. */
export const applyInitialTheme = applyInitialCustomerTheme;

export const setTheme = createThemeApi(THEME_STORAGE_KEYS.customer).setTheme;

export const getThemePreference = createThemeApi(THEME_STORAGE_KEYS.customer).getThemePreference;

export const getEffectiveTheme = createThemeApi(THEME_STORAGE_KEYS.customer).getEffectiveTheme;

export const subscribeToSystemTheme =
  createThemeApi(THEME_STORAGE_KEYS.customer).subscribeToSystemTheme;

export function ThemeProvider({
  children,
  lightOverrideEnabled = false,
}: ThemeProviderProps): JSX.Element {
  return (
    <SharedThemeProvider
      storageKey={THEME_STORAGE_KEYS.customer}
      lightOverrideEnabled={lightOverrideEnabled}
    >
      {children}
    </SharedThemeProvider>
  );
}
